/* eslint-disable react-refresh/only-export-components -- фабрика редакторов ячеек, не hot-reload модуль */
import { KeyboardEvent, ReactNode, useRef } from 'react';
import { IDataTableEditorContext } from '@srm/ui/data-table';
import Input from '@srm/ui/input';
import InputNumber from '@srm/ui/input-number';
import Textarea from '@srm/ui/textarea';
import Select from '@srm/ui/select';
import Dictpicker from '@atoms/Dictpicker/Dictpicker';
import DictpickerModal from '@atoms/Dictpicker/DictpickerModal/DictpickerModal';
import Abookpicker from '@atoms/Abookpicker/Abookpicker';
import AbookpickerModal from '@atoms/Abookpicker/AbookpickerModal/AbookpickerModal';
import AutoCompletePicker from '@srm/ui/auto-complete-picker';
import TimeField from '@srm/ui/time-field';
import { getOptions } from '@molecules/formbuilder/controls/AutoComplete/AutoCompleteHelper';
import { parseDisplayFormatCell } from '@utils/documentUtils';
import AbookCellView from './AbookCellView';
import { IOption } from '@srm/ui/types/IOption';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import {
    ITableColumn,
    ITableColumnAbook,
    ITableColumnAutoComplete,
    ITableColumnDict,
    TableColumn,
} from '@models/Forms/IForms';
import { ValueType } from '@srm/ui/types/ValueType';
import DateCellEditor from './DateCellEditor';
import { isInPortalOverlay, portalOverlayClassName } from '@srm/ui/lib/portalOverlay';
import { TableColumnKind, TableRow } from './tableColumns';
import { TableFormBridge } from './tableFormBridge';
import { useResolvedAbookTabs } from '@/hooks/useResolvedAbookTabs';
import { resolveVisibleAbookTabs } from '@utils/abookTabVisibility';

/** Прекэшированные значения асинхронных правил колонки для конкретной строки. */
export interface IEditorRuleValues {
    minDate?: string;
    maxDate?: string;
    workCalendar?: boolean;
}

type Ctx = IDataTableEditorContext<TableRow>;

/**
 * Обработчик выбора в выпадающем списке ячейки. Одиночный выбор пишет строку и закрывает редактор. Множественный
 * пишет каждую отметку и редактор не закрывает: копить отметки до закрытия списка нельзя — клик вне ячейки
 * размонтирует редактор раньше, чем список узнает, что закрыт, а окно из списка показало бы старое значение.
 */
const cellPickerChange =
    <T,>(
        isMultiple: boolean | undefined,
        toPatch: (items: T[]) => Promise<Record<string, unknown>>,
        onCommitPatch: (patch: Record<string, unknown>) => void,
        onWritePatch: (patch: Record<string, unknown>) => void,
    ) =>
    async (items: T[]) =>
        (isMultiple ? onWritePatch : onCommitPatch)(await toPatch(items));

/**
 * Справочник: клик по ячейке сразу открывает модалку выбора — без промежуточного
 * инпута и второго клика (старая DX-ветка требовала клик по ячейке, затем клик по
 * пикеру; автофокус-фиксы откатывались из-за проблем с событиями). Выбор строит
 * патч строки (значение + setValues.sets) и уходит в общий пайплайн коммита.
 */
export const DictCellEditor = ({
    column,
    row,
    docId,
    bridge,
    onCommitPatch,
    onWritePatch,
    onCancel,
}: {
    column: ITableColumnDict;
    row: TableRow;
    docId?: string;
    bridge: TableFormBridge;
    onCommitPatch: (patch: Record<string, unknown>) => void;
    /** Запись строки без закрытия редактора (множественный выбор). */
    onWritePatch: (patch: Record<string, unknown>) => void;
    onCancel: () => void;
}) => {
    const onPickerChange = cellPickerChange<IDictionaryData>(
        column.isMultiple,
        (items) => bridge.dictSelectionPatch(column, row, items),
        onCommitPatch,
        onWritePatch,
    );
    // Колонка в режиме выпадающего списка (isSelectMode) выбирает значение прямо в
    // ячейке — модалка справочника для короткого списка избыточна (справочник-дерево
    // всё равно выбирается в окне). autoFocus у пикера раскрывает дропдаун сразу,
    // поэтому клик по ячейке остаётся один.
    return column.isSelectMode ? (
        <Dictpicker
            insideGrid
            autoFocus
            isSelectMode
            name={column.key}
            dictionaryName={column.dictName}
            modalTitle={column.modalTitle}
            isFormData={!!column.isFormData}
            isMultiple={!!column.isMultiple}
            recursive={column.recursive}
            selectableLevels={column.selectableLevels}
            visibleLevels={column.visibleLevels}
            predicatesCache={column.predicatesCache}
            loadMode={column.loadMode}
            useClientSideDataProcessing={column.useClientSideDataProcessing}
            gridAttribute={column.gridAttribute}
            externalSearch={column.externalSearch}
            addForm={column.addForm}
            userScripts={column.userScripts}
            wordWrapEnabled={column.wordWrapEnabled}
            showActionsBar={column.showActionsBar}
            source={column.source}
            placeholder={column.placeholder}
            getExternalDataSource={column.externalDataSource ? () => bridge.getDictExternalData(column) : undefined}
            getFormValuesAsync={() => bridge.getFormValuesAsync(column.formValues, row)}
            getFiltersAsync={() => bridge.getFiltersAsync(column.filters, row)}
            defaultValue={row[column.key]}
            displayValue={String(parseDisplayFormatCell(column.displayFormat, row, column.evalDisplayFormat) ?? '')}
            onChange={onPickerChange}
            onCancel={onCancel}
        />
    ) : (
        <>
            {/* пока модалка открыта, ячейка держит текущее значение — не пустоту */}
            <div className="tdn-cell">
                <div>{parseDisplayFormatCell(column.displayFormat, row, column.evalDisplayFormat)}</div>
            </div>
            <DictpickerModal
                docId={docId}
                dictName={column.dictName}
                modalTitle={column.modalTitle}
                isFormData={column.isFormData}
                isMultiple={column.isMultiple}
                recursive={column.recursive}
                selectableLevels={column.selectableLevels ?? ''}
                visibleLevels={column.visibleLevels ?? ''}
                predicatesCache={column.predicatesCache ?? ''}
                // ВАЖНО: без loadMode из конфигурации — постраничная загрузка; дефолт
                // 'all' отключал пагинацию и рендерил весь справочник (тысячи строк —
                // страница зависала на десятки секунд)
                loadMode={column.loadMode ?? ''}
                useClientSideDataProcessing={column.useClientSideDataProcessing}
                gridAttribute={column.gridAttribute}
                externalSearch={column.externalSearch}
                addForm={column.addForm}
                userScripts={column.userScripts}
                wordWrapEnabled={column.wordWrapEnabled}
                showActionsBar={column.showActionsBar}
                getExternalDataSource={column.externalDataSource ? () => bridge.getDictExternalData(column) : undefined}
                selected={String(row[column.key] ?? '')
                    .split('|')
                    .filter(Boolean)}
                getFormValuesAsync={() => bridge.getFormValuesAsync(column.formValues, row)}
                getFiltersAsync={() => bridge.getFiltersAsync(column.filters, row)}
                onSubmit={async (items) => onCommitPatch(await bridge.dictSelectionPatch(column, row, items))}
                onClose={onCancel}
            />
        </>
    );
};

/** Адресная книга: как справочник — список или модалка выбора сразу по первому клику. */
export const AbookCellEditor = ({
    column,
    row,
    docId,
    bridge,
    onCommitPatch,
    onWritePatch,
    onCancel,
}: {
    column: ITableColumnAbook;
    row: TableRow;
    docId?: string;
    bridge: TableFormBridge;
    onCommitPatch: (patch: Record<string, unknown>) => void;
    /** Запись строки без закрытия редактора (множественный выбор). */
    onWritePatch: (patch: Record<string, unknown>) => void;
    onCancel: () => void;
}) => {
    // условия вкладок, как и правила строки, видят и поля строки, и поля документа
    const tabsSettings = useResolvedAbookTabs(
        column.tabs,
        (source) =>
            resolveVisibleAbookTabs(source, {
                evalBool: async (rule, fallback) => {
                    if (!rule) return fallback;
                    const value = await bridge.evalTableFormulaValue(rule, row);
                    return value === undefined || value === null ? fallback : Boolean(value);
                },
            }),
        row,
    );
    const toPatch = (items: IAddressBookData[]) => bridge.abookSelectionPatch(column, row, items as never);
    const onPickerChange = cellPickerChange(column.isMultiple, toPatch, onCommitPatch, onWritePatch);
    return column.isSelectMode ? (
        <Abookpicker
            insideGrid
            autoFocus
            isSelectMode
            name={column.key}
            title={column.name}
            isMultiple={!!column.isMultiple}
            recursive={column.recursive}
            tabsSettings={tabsSettings}
            externalSearch={column.externalSearch}
            gridAttribute={column.gridAttribute}
            showChips={column.showChips}
            wordWrapEnabled={column.wordWrapEnabled}
            showActionsBar={column.showActionsBar}
            placeholder={column.placeholder}
            docId={docId}
            formValues=""
            getFormValues={() => bridge.getFormValues(column.formValues, row)}
            defaultValue={row[column.key]}
            displayValue={String(parseDisplayFormatCell(column.displayFormat, row, column.evalDisplayFormat) ?? '')}
            onChange={onPickerChange}
            onCancel={onCancel}
        />
    ) : (
        <>
            {/* пока модалка открыта, ячейка держит текущее значение — не пустоту */}
            <div className="tdn-cell">
                <AbookCellView column={column} p={{ data: row, value: row[column.key] }} />
            </div>
            <AbookpickerModal
                docId={docId}
                title={column.name}
                isMultiple={column.isMultiple}
                recursive={column.recursive}
                tabsSettings={tabsSettings}
                externalSearch={column.externalSearch}
                gridAttribute={column.gridAttribute}
                showChips={column.showChips}
                wordWrapEnabled={column.wordWrapEnabled}
                showActionsBar={column.showActionsBar}
                formValues={bridge.getFormValues(column.formValues, row)}
                selected={String(row[column.key] ?? '')
                    .split('|')
                    .filter(Boolean)
                    .map(Number)}
                onSubmit={async (items) => onCommitPatch(await toPatch(items))}
                onClose={onCancel}
            />
        </>
    );
};

/** Автокомплит: наш пикер с подсказками, коммит по blur/Enter. */
const AutoCompleteEditor = ({ column, ctx }: { column: ITableColumnAutoComplete; ctx: Ctx }) => {
    const last = useRef<string>(String(ctx.value ?? ''));
    return (
        <div
            className="tdn-cell-editor--single-line"
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    e.stopPropagation();
                    ctx.cancel();
                } else if (e.key === 'Enter') {
                    ctx.commit(last.current);
                }
            }}
            onBlur={(e) => {
                const rt = e.relatedTarget as HTMLElement | null;
                if (rt && e.currentTarget.contains(rt)) return;
                ctx.commit(last.current);
            }}
        >
            <AutoCompletePicker
                defInputValue={String(ctx.value ?? '')}
                debounceTime={500}
                placeholder={column.placeholder || undefined}
                findOptions={async (text: string) => getOptions(column.dataSource!, text)}
                onSearchChange={(value: string) => {
                    last.current = value;
                }}
            />
        </div>
    );
};

/** Время: TimeField, коммит по уходу фокуса (клики в поповер помечены и не закрывают). */
const TimeEditor = ({ column, ctx }: { column: ITableColumn; ctx: Ctx }) => {
    const last = useRef<string>(String(ctx.value ?? ''));
    return (
        <div
            onKeyDown={stopEsc(ctx.cancel)}
            onBlur={(e) => {
                const rt = e.relatedTarget as HTMLElement | null;
                if (!rt) return;
                if (e.currentTarget.contains(rt) || isInPortalOverlay(rt)) return;
                ctx.commit(last.current);
            }}
        >
            <TimeField
                name={column.key}
                value={String(ctx.value ?? '')}
                onChange={(next: string) => {
                    last.current = next;
                }}
                variant="unstyled"
                popoverProps={{ classNames: { dropdown: portalOverlayClassName } }}
            />
        </div>
    );
};

/** Типы, для которых таблица даёт кастомный редактор (наши контролы);
 *  остальные пока редактируются дефолтными редакторами атома. */
export const hasCustomTableEditor = (kind: TableColumnKind, valueType: ValueType): boolean => {
    if (kind === 'dict' || kind === 'abook' || kind === 'autocomplete' || kind === 'calc') return true;
    if (kind !== 'field' && kind !== 'exds') return false;
    return [
        ValueType.Text,
        ValueType.Url,
        ValueType.LongText,
        ValueType.Double,
        ValueType.Money,
        ValueType.Integer,
        ValueType.Boolean,
        ValueType.Year,
        ValueType.YesNo,
        ValueType.Date,
        ValueType.DateTime,
        ValueType.NoSecDateTime,
        ValueType.Time,
    ].includes(valueType);
};

const stopEsc = (cancel: () => void) => (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
        e.stopPropagation();
        cancel();
    }
};

/** Текст/URL: uncontrolled, trim и коммит по blur/Enter (паритет старого редактора). */
const TextEditor = ({ column, ctx, maxLength }: { column: ITableColumn; ctx: Ctx; maxLength?: number }) => {
    const last = useRef<string>(String(ctx.value ?? ''));
    const commit = () => ctx.commit(last.current.trim());
    return (
        <div
            className="tdn-cell-editor--single-line"
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    e.stopPropagation();
                    ctx.cancel();
                } else if (e.key === 'Enter') {
                    e.preventDefault();
                    commit();
                }
            }}
        >
            <Input
                maxLength={maxLength}
                pattern={column.inputRegExp}
                name={column.key}
                defaultValue={String(ctx.value ?? '')}
                onChange={(e) => {
                    last.current = e.currentTarget.value;
                }}
                onBlur={commit}
                placeholder={column.placeholder || undefined}
                autoFocus
            />
        </div>
    );
};

/** Число (Integer/Double/Money): InputNumber, коммит по blur/Enter. */
const NumberEditor = ({ column, ctx }: { column: ITableColumn; ctx: Ctx }) => {
    const isInteger = column.valueType === ValueType.Integer;
    const isDouble = column.valueType === ValueType.Double;
    const last = useRef<unknown>(ctx.value);
    const commit = () => ctx.commit(last.current);
    return (
        <div
            className="tdn-cell-editor--single-line"
            onBlur={(e) => {
                // blur наружу редактора (не между внутренними узлами InputNumber)
                if (!e.currentTarget.contains(e.relatedTarget as Node)) commit();
            }}
        >
            <InputNumber
                name={column.key}
                {...(isInteger || isDouble ? { groupBy: 0, separator: '' } : {})}
                {...(isInteger ? {} : { floatPoints: column.floatPoints ?? 2 })}
                defaultValue={ctx.value as never}
                onValueChange={(value: string) => {
                    const val = +value.replace(/\s/g, '');
                    last.current = Number.isNaN(val) ? null : val;
                }}
                // InputNumber гасит Enter (preventDefault+stopPropagation) до обёртки,
                // но пробрасывает свой onKeyDown — вешаем клавиши на него
                onKeyDown={(e) => {
                    if (e.key === 'Enter') commit();
                    else if (e.key === 'Escape') {
                        e.stopPropagation();
                        ctx.cancel();
                    }
                }}
                placeholder={column.placeholder || undefined}
                autoFocus
            />
        </div>
    );
};

/** Многострочный текст: коммит по blur (Enter — перенос строки). */
const LongTextEditor = ({ column, ctx }: { column: ITableColumn; ctx: Ctx }) => {
    const last = useRef<string>(String(ctx.value ?? ''));
    return (
        <div onKeyDown={stopEsc(ctx.cancel)}>
            <Textarea
                autoResize
                initialRowCount={1}
                name={column.key}
                defaultValue={String(ctx.value ?? '')}
                onValueChange={(value: string) => {
                    last.current = value;
                }}
                onBlur={() => ctx.commit(last.current.trim())}
                placeholder={column.placeholder || undefined}
                maxLength={column.maxLength}
                autoFocus
            />
        </div>
    );
};

const yearOptions = (column: ITableColumn): IOption[] => {
    let startYear = 1950;
    let countItems = 100;
    if (column.min && Number(column.min) > 1950) startYear = Number(column.min);
    if (column.max) countItems = Number(column.max) - startYear;
    return Array.from({ length: countItems + 1 }, (_, i) => {
        const year = String(startYear + i);
        return { label: year, value: year } as IOption;
    });
};

/**
 * Кастомный редактор ячейки таблицы форм. Значения — в паритете со старым
 * EditTable: Boolean пишет 1/0, YesNo — '1'/'0', текст триммится при коммите.
 */
export const renderTableEditor = (
    kind: TableColumnKind,
    column: TableColumn,
    ctx: Ctx,
    rules: IEditorRuleValues = {},
): ReactNode => {
    const col = column as ITableColumn;
    if (kind === 'autocomplete') {
        return <AutoCompleteEditor column={column as ITableColumnAutoComplete} ctx={ctx} />;
    }
    if (kind === 'calc') {
        // паритет renderColumnCalcGrid: число, без valueType — целое (как и показ
        // calc-ячейки); текстовый calc calculateRow хранит строкой — правим текстом
        if (col.valueType === ValueType.Text) return <TextEditor column={col} ctx={ctx} maxLength={255} />;
        const valueType =
            col.valueType === ValueType.Double || col.valueType === ValueType.Money ? col.valueType : ValueType.Integer;
        return <NumberEditor column={{ ...col, valueType }} ctx={ctx} />;
    }
    switch (col.valueType) {
        case ValueType.Year:
            return (
                // Escape закрывает не только список, но и редактор ячейки: список
                // гасит событие сам, поэтому обработчик стоит на обёртке.
                <div onKeyDown={stopEsc(ctx.cancel)}>
                    <Select
                        options={yearOptions(col)}
                        onlySelect
                        values={
                            ctx.value != null && ctx.value !== ''
                                ? [{ value: String(ctx.value), label: String(ctx.value) } as IOption]
                                : undefined
                        }
                        onChange={(option: IOption[]) => {
                            if (option.length > 0) ctx.commit(+option[0].value);
                        }}
                        placeholder={col.placeholder || undefined}
                    />
                </div>
            );
        case ValueType.YesNo: {
            const label = (v: unknown) =>
                String(v) === '1' ? (col.trueText ?? 'Да') : String(v) === '0' ? (col.falseText ?? 'Нет') : '';
            return (
                <div onKeyDown={stopEsc(ctx.cancel)}>
                    <Select
                        options={[
                            { value: '1', label: col.trueText ?? 'Да' },
                            { value: '0', label: col.falseText ?? 'Нет' },
                        ]}
                        onlySelect
                        readOnly
                        values={[{ value: String(ctx.value ?? ''), label: label(ctx.value) } as IOption]}
                        onChange={(option: IOption[]) => {
                            if (option.length > 0) ctx.commit(option[0].value);
                        }}
                        placeholder={col.placeholder || undefined}
                    />
                </div>
            );
        }
        case ValueType.LongText:
            return <LongTextEditor column={col} ctx={ctx} />;
        case ValueType.Double:
        case ValueType.Money:
        case ValueType.Integer:
            return <NumberEditor column={col} ctx={ctx} />;
        case ValueType.Date:
        case ValueType.DateTime:
        case ValueType.NoSecDateTime:
            return (
                <DateCellEditor
                    name={col.key}
                    value={ctx.value as never}
                    minDate={rules.minDate}
                    maxDate={rules.maxDate}
                    workCalendar={rules.workCalendar ?? false}
                    placeholder={col.placeholder || undefined}
                    setValue={(value: unknown) => ctx.commit(value)}
                />
            );
        case ValueType.Time:
            return <TimeEditor column={col} ctx={ctx} />;
        case ValueType.Url:
            // Несколько адресов по строкам: однострочный <input> склеил бы их при правке.
            return String(ctx.value ?? '').includes('\n') ? (
                <LongTextEditor column={col} ctx={ctx} />
            ) : (
                <TextEditor column={col} ctx={ctx} />
            );
        case ValueType.Text:
        default:
            return <TextEditor column={col} ctx={ctx} maxLength={255} />;
    }
};
